import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaLeaf, FaQuestion, FaMapMarkerAlt, FaClock } from 'react-icons/fa';

// Updated hero background - more immersive forest therapy setting
const heroBackground = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const WildernessContainer = styled.div`
  color: ${theme.colors.textLight};
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(${theme.colors.primaryRGB}, 0.8)
  ), url(${heroBackground}) no-repeat center center;
  background-size: cover;
  height: 90vh; 
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px;
  
  @media screen and (max-width: 768px) {
    height: 80vh;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin-top: 80px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 24px;
  letter-spacing: 1px;
  font-family: ${theme.fonts.heading};
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroTagline = styled.p`
  font-size: 1.5rem;
  color: ${theme.colors.light};
  margin: 0 auto 40px;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  background: ${theme.colors.dark};
  padding: ${theme.spacing.section} 0;
`;

const SectionWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 40px;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -10px;
    left: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: 25px;
`;

const Emphasis = styled.em`
  display: block;
  font-size: 1.2rem;
  margin: 30px 0;
  color: ${theme.colors.accent};
`;

const Strong = styled.strong`
  color: ${theme.colors.accent};
`;

// New component for image and text layout
const ContentWithImage = styled.div`
  display: flex;
  align-items: center;
  margin: 50px 0;
  flex-direction: ${props => props.imageRight ? 'row-reverse' : 'row'};
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  padding: ${props => props.imageRight ? '0 0 0 30px' : '0 30px 0 0'};
  
  @media screen and (max-width: 768px) {
    padding: 0;
    margin-bottom: 30px;
    width: 100%;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  object-fit: cover;
  height: 300px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const FormatContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 30px;
  margin: 40px 0;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormatCard = styled.div`
  background: ${theme.colors.primaryDark};
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const FormatTitle = styled.h3`
  font-size: 1.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
`;

const FormatText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const BookButton = styled.a`
  display: inline-block;
  background: ${theme.colors.accent};
  color: white;
  padding: 15px 30px;
  font-size: 1.1rem;
  border-radius: ${theme.borderRadius};
  text-decoration: none;
  margin: 30px 0;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.accentLight};
    transform: translateY(-3px);
  }
`;

const FAQSection = styled.section`
  background: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryDark});
  padding: ${theme.spacing.section} 0;
`;

const FAQContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FAQTitle = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.textLight};
  margin-bottom: 50px;
  text-align: center;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const FAQItem = styled.div`
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius};
  padding: 25px;
  backdrop-filter: blur(5px);
`;

const FAQQuestion = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
  }
`;

const FAQAnswer = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
`;

const WildernessPsychotherapySection = () => {
  return (
    <WildernessContainer id="wilderness-therapy">
      <HeroSection>
        <HeroContent>
          <HeroTitle>1:1 Wilderness Psychotherapy</HeroTitle>
          <HeroTagline>Come as you are. Take what you need.</HeroTagline>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <SectionWrapper>
          <SectionHeading>This isn't therapy in a traditional sense.</SectionHeading>
          
          <ContentWithImage imageRight={false}>
            <ImageContainer>
              <ContentImage 
                src={`${process.env.PUBLIC_URL}/images/campfire-therapy.jpg`}
                alt="Two men having a conversation around a campfire in the woods" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                This is work that honours your body, your boundaries, and your becoming.
              </Paragraph>
              
              <Paragraph>
                If you're looking for something more embodied, more connected, and more you - you're in the right place.
              </Paragraph>
              
              <Emphasis>
                Book a free 15 minute discovery call here, lets see if we connect, no pressure no expectation.
              </Emphasis>
              
              <BookButton href="#contact">Book a Discovery Call</BookButton>
            </TextContent>
          </ContentWithImage>
        </SectionWrapper>
      </ContentSection>
      
      <FAQSection>
        <FAQContainer>
          <FAQTitle>FAQ</FAQTitle>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> Do I need to be "outdoorsy" to try this?
            </FAQQuestion>
            <FAQAnswer>
              Not at all. You don't need hiking boots, survival skills, or a love for mud. 
              Just a willingness to show up as you are. We'll go at your pace, and I'll guide you through what to expect.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> What if the weather's bad?
            </FAQQuestion>
            <FAQAnswer>
              We'll always have a plan B. I work with sheltered outdoor spots, and we can also shift to indoor 
              or online sessions if needed. Nature is unpredictable - part of the work is learning to move with it, not against it.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> Is this like traditional therapy?
            </FAQQuestion>
            <FAQAnswer>
              Yes and no. The foundations are the same - deep listening, compassion, professional ethics but 
              nature adds something special. It helps us access emotion and meaning in a different way. It's more embodied. More spacious.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaClock /> How long are the sessions?
            </FAQQuestion>
            <FAQAnswer>
              Typically 60 minutes, though we can extend to 90 if needed. Longer sessions are available for deeper process work.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> What kinds of things can I bring to a session?
            </FAQQuestion>
            <FAQAnswer>
              Anything you're carrying such as anxiety, grief, burnout, life transitions, identity, relationships, trauma, 
              or just a sense of "something's not right." You don't need to have it all figured out. That's what the space is for.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaMapMarkerAlt /> Where do sessions take place?
            </FAQQuestion>
            <FAQAnswer>
              I use a few trusted nature spots in the local area (TBC depending on your location), or we can work indoors or online. 
              We'll decide together based on what feels right for you.
            </FAQAnswer>
          </FAQItem>
          
          <FAQItem>
            <FAQQuestion>
              <FaQuestion /> Can I try one session before committing?
            </FAQQuestion>
            <FAQAnswer>
              Absolutely. A first session is a good way to feel it out — there's no pressure to continue if it doesn't feel like the right fit. 
              If we're not a match I bet I know someone that is.
            </FAQAnswer>
          </FAQItem>
        </FAQContainer>
      </FAQSection>
    </WildernessContainer>
  );
};

export default WildernessPsychotherapySection;